// src/modules/app-views/account/pages/Init/modals/ChangeEmailModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Alert, Typography } from 'antd';
import { MailOutlined, NumberOutlined } from '@ant-design/icons';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import appService from 'services/appService';
import authService from 'services/authService';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const VerifyCurrentEmailStepContent = ({
  storedUser,
  currentEmail,
  codeSent,
  setCodeSent,
}) => {
  const [isSending, setIsSending] = useState(false);

  // Send code automatically when step opens
  useEffect(() => {
    if (!codeSent && currentEmail && !isSending) {
      sendVerificationCode();
    }
  }, [codeSent, currentEmail]);

  const sendVerificationCode = async () => {
    if (isSending) return;

    setIsSending(true);

    try {
      const success = await authService.sendEmailRegistrationCode(
        {
          name: storedUser?.name,
          email: currentEmail,
          emailData: { highTitle: 'verify_email' },
        },
        'just_send_email'
      );

      if (success && success !== 'error') {
        setCodeSent(true);

        appService.notification({
          key: 'emailCodeSentKey',
          flag: 'info',
          title: currentEmail,
          description: 'verification_code_sent_email',
          duration: 0,
          icon: <MailOutlined />,
        });
      } else {
        await appService.message({ flag: 'e', text: 'failed_to_send_verification_code' });
      }
    } catch (err) {
      console.error('[ChangeEmailModal] Error sending code:', err);
      await appService.message({ flag: 'e', text: 'failed_to_send_verification_code' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        icon={<MailOutlined />}
        title={translateX('verify_current_email')}
        description={translateX('verify_current_email_description', {
          '%EMAIL%': currentEmail,
        })}
        style={{ marginBottom: 16 }}
      />

      <SimpleInput
        name="currentEmailCode"
        label="verification_code"
        minLength={4}
        maxLength={4}
        required
        prefix={<NumberOutlined className="text-primary" />}
        placeholder="0000"
      />

      {codeSent && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {translateX('didnt_receive_code')}{' '}
          <a onClick={sendVerificationCode} style={{ cursor: isSending ? 'wait' : 'pointer' }}>
            {translateX('resend_code')}
          </a>
        </Text>
      )}
    </>
  );
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
const validateCurrentEmailCode = async ({
  form,
  setLoading,
  goFocus,
  currentEmail,
}) => {
  const code = form?.getFieldValue('currentEmailCode');

  if (!code || code.trim() === '') {
    await appService.message({ flag: 'w', text: 'verification_code_required' });
    goFocus?.('currentEmailCode', true);
    return false;
  }

  try {
    setLoading(true);

    const success = await authService.validateEmailCode({
      email: currentEmail,
      emailCode: code,
    });

    if (success) {
      appService.notificationDestroy('emailCodeSentKey');
      return true;
    } else {
      await appService.message({ flag: 'w', text: 'invalid_verification_code' });
      goFocus?.('currentEmailCode', true);
      return false;
    }
  } catch (err) {
    console.error('[ChangeEmailModal] Error validating code:', err);
    await appService.message({ flag: 'e', text: 'verification_failed' });
    goFocus?.('currentEmailCode', true);
    return false;
  } finally {
    setLoading(false);
  }
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const verifyCurrentEmailStep = ({ storedUser, currentEmail }) => {
  let codeSent = false;

  return {
    stepTitle: 'current_email',
    stepTooltip: 'verify_current_email',
    content: ({ form, goFocus }) => {
      const [localCodeSent, setLocalCodeSent] = useState(codeSent);

      // Sync with closure
      useEffect(() => {
        codeSent = localCodeSent;
      }, [localCodeSent]);

      return (
        <VerifyCurrentEmailStepContent
          storedUser={storedUser}
          currentEmail={currentEmail}
          codeSent={localCodeSent}
          setCodeSent={setLocalCodeSent}
        />
      );
    },
    validationFields: ['currentEmailCode'],
    onNext: async ({ form, setLoading, goFocus }) =>
      validateCurrentEmailCode({
        form,
        setLoading,
        goFocus,
        currentEmail,
      }),
    onEnter: ({ goFocus }) => {
      setTimeout(() => goFocus?.('currentEmailCode'), 100);
    },
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default verifyCurrentEmailStep;
